/**
 * Business hours: how much of a span falls inside a client's opening hours ("זמן המתנה בשעות הפעילות").
 * A lead that arrives Thursday at 21:00 and is still waiting Sunday at 10:00 has waited one business hour, not 61.
 */

/** Opening and closing time ("HH:MM", local), or null for a closed day. */
export type DayHours = [open: string, close: string] | null;
/** Weekly schedule in the client's timezone; week[0] is Sunday. Holidays come in separately, as per-date overrides. */
export type BusinessHours = { tz: string; week: DayHours[] };

/** Default until a client sets its own (Pro Car's hours): Sun–Thu 08:30–18:30, Fri 08:00–14:00, Saturday closed. */
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  tz: 'Asia/Jerusalem',
  week: [['08:30', '18:30'], ['08:30', '18:30'], ['08:30', '18:30'], ['08:30', '18:30'], ['08:30', '18:30'], ['08:00', '14:00'], null],
};

export const WEEKDAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Accepts a stored value if it is well-formed, otherwise the default. */
export function parseBusinessHours(v: unknown): BusinessHours {
  const b = v as Partial<BusinessHours> | null | undefined;
  if (!b || typeof b.tz !== 'string' || !Array.isArray(b.week) || b.week.length !== 7) return DEFAULT_BUSINESS_HOURS;
  const week = b.week.map((d) => (Array.isArray(d) && d.length === 2 && HHMM.test(String(d[0])) && HHMM.test(String(d[1])) && d[0] < d[1] ? [String(d[0]), String(d[1])] as [string, string] : null));
  try { new Intl.DateTimeFormat('en-GB', { timeZone: b.tz }); } catch { return DEFAULT_BUSINESS_HOURS; }
  return { tz: b.tz, week };
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
/** Offset of the timezone from UTC at an instant, in ms (Jerusalem: +2h or +3h). */
export function offsetMs(epoch: number, tz: string): number {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    fmtCache.set(tz, f);
  }
  const p: Record<string, number> = {};
  for (const x of f.formatToParts(new Date(epoch))) if (x.type !== 'literal') p[x.type] = Number(x.value);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(epoch / 1000) * 1000;
}

const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const DAY = 86_400_000;

/** Midnight of the local day containing `epoch`, in the given timezone ("today" means today in Israel, wherever the phone is). */
export function startOfLocalDay(epoch: number, tz = 'Asia/Jerusalem'): number {
  const localDay = Math.floor((epoch + offsetMs(epoch, tz)) / DAY) * DAY;
  const guess = localDay - offsetMs(epoch, tz);
  return localDay - offsetMs(guess, tz); // re-read the offset at midnight itself (DST switches at 02:00, after it)
}

/** Weekly open hours, e.g. for a settings summary. */
export function weeklyHours(bh: BusinessHours): number {
  return bh.week.reduce((sum, d) => sum + (d ? (minutes(d[1]) - minutes(d[0])) / 60 : 0), 0);
}

/**
 * Milliseconds of [from, to) that fall within opening hours. Works on local wall-clock time: each business day is a fixed
 * local interval, and DST switches happen at night, outside any opening hours, so they never change the result.
 */
export function businessMsBetween(from: number, to: number, bh: BusinessHours = DEFAULT_BUSINESS_HOURS, overrides?: Map<string, DayHours>): number {
  if (!(to > from)) return 0;
  const f = from + offsetMs(from, bh.tz); // local wall-clock, expressed as if it were UTC
  const t = to + offsetMs(to, bh.tz);
  let total = 0;
  for (let day = Math.floor(f / DAY) * DAY; day < t; day += DAY) {
    // a holiday's hours (or closure) replace the weekday's; see holidayOverrides()
    const key = overrides?.size ? new Date(day).toISOString().slice(0, 10) : '';
    const hours = key && overrides!.has(key) ? overrides!.get(key)! : bh.week[new Date(day).getUTCDay()];
    if (!hours) continue;
    const open = day + minutes(hours[0]) * 60_000;
    const close = day + minutes(hours[1]) * 60_000;
    total += Math.max(0, Math.min(close, t) - Math.max(open, f));
  }
  return total;
}

/** Is the business open at this moment (weekly hours, with holiday overrides)? */
export function isOpenAt(t: number, bh: BusinessHours = DEFAULT_BUSINESS_HOURS, overrides?: Map<string, DayHours>): boolean {
  return businessMsBetween(t, t + 60_000, bh, overrides) > 0;
}
